// The user guide: a "How to" side panel for the tab you are on, and the full Guide page.
// Everything is filtered to the reader's role and capabilities, exactly as the sidebar is,
// so a guide never describes a tab or action its reader cannot see.
import { createContext, Fragment, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useOutletContext } from 'react-router-dom';
import { BookOpen, CircleHelp, ExternalLink, X } from 'lucide-react';
import { NAV } from '@/lib/nav';
import type { Program } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge, Card } from '@/components/ui/misc';
import { GUIDE } from './content';
import type { GuideSection, GuideStep, GuideTask } from './types';

type Gate = { roles?: string[]; requires?: string };
const allowed = (p: Program, g: Gate) =>
  (!g.roles || g.roles.includes(p.membership.role)) &&
  (!g.requires || p.capabilities.includes(g.requires as Program['capabilities'][number]));

/** The sections this member may read, in sidebar order, with tasks and steps filtered. */
export function guideFor(p: Program): GuideSection[] {
  const order = new Map(NAV.map((n, i) => [n.path, i]));
  const navGate = new Map(NAV.map((n) => [n.path, n.requires]));
  return GUIDE.filter((s) => allowed(p, { requires: navGate.get(s.id) }))
    .map((s) => ({
      ...s,
      notes: s.notes?.filter((n) => allowed(p, n)),
      tasks: s.tasks
        .filter((t) => allowed(p, t))
        .map((t) => ({ ...t, steps: t.steps.filter((st) => allowed(p, st)) }))
        .filter((t) => t.steps.length > 0),
    }))
    .filter((s) => s.tasks.length > 0)
    .sort((a, b) => (order.get(a.id) ?? 99) - (order.get(b.id) ?? 99));
}

/** The client short name, filled in wherever the guide writes {client}. */
const ClientName = createContext('the client');

/** Straight-quoted fragments are on-screen labels: shown as chips. */
function Rich({ text: raw }: { text: string }) {
  const text = raw.replace(/\{client\}/g, useContext(ClientName));
  const parts = text.split(/"([^"]+)"/);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 ? (
          <span
            key={i}
            className="rounded border border-border bg-surface-2 px-1 py-px text-[0.92em] font-medium text-text"
          >
            {part}
          </span>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

function Step({ s, n }: { s: GuideStep; n: number }) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-brand-soft text-[11px] font-semibold text-brand-text">
        {n}
      </span>
      <div className="min-w-0 flex-1 text-[13.5px] leading-relaxed">
        <p>
          <Rich text={s.t} />
          {s.where === 'outside' && (
            <Badge tone="warn" className="ml-2 align-middle">
              Outside HELM
            </Badge>
          )}
        </p>
        {s.d && (
          <p className="mt-0.5 text-[13px] text-muted">
            <Rich text={s.d} />
          </p>
        )}
      </div>
    </li>
  );
}

function Task({ t }: { t: GuideTask }) {
  return (
    <div>
      <h4 className="mb-2 text-[14px] font-semibold">
        <Rich text={t.title} />
      </h4>
      <ol className="space-y-2.5">
        {t.steps.map((s, i) => (
          <Step key={i} s={s} n={i + 1} />
        ))}
      </ol>
    </div>
  );
}

function SectionBody({ s }: { s: GuideSection }) {
  return (
    <div className="space-y-5">
      <p className="text-[13.5px] text-muted">
        <Rich text={s.purpose} />
      </p>
      {s.tasks.map((t) => (
        <Task key={t.title} t={t} />
      ))}
      {s.notes && s.notes.length > 0 && (
        <div className="rounded-md border border-border bg-surface-2/60 px-3 py-2.5">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Good to know</p>
          <ul className="list-disc space-y-1 pl-4 text-[13px] text-muted">
            {s.notes.map((n, i) => (
              <li key={i}>
                <Rich text={n.text} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** The tab a path belongs to: /p/:id/<tab>/... */
const tabOf = (pathname: string) => pathname.split('/')[3] ?? '';

/** "How to" in the header: the steps for the tab you are on. */
export function GuideButton({ program }: { program: Program }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const tab = tabOf(pathname);
  const section = guideFor(program).find((s) => s.id === tab);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open]);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text"
        aria-haspopup="dialog"
      >
        <CircleHelp className="size-4" /> How to
      </button>
      {open && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onClick={() => setOpen(false)}>
          <aside
            role="dialog"
            aria-label="How to"
            className="flex h-full w-full max-w-[460px] flex-col border-l border-border bg-surface shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-border px-5 py-3">
              <CircleHelp className="size-4 text-brand-text" />
              <h2 className="text-[15px] font-semibold">How to: {section?.title ?? 'this page'}</h2>
              <Button variant="ghost" size="icon" className="ml-auto" aria-label="Close" onClick={() => setOpen(false)}>
                <X />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {section ? (
                <ClientName.Provider value={program.clientShortName}>
                  <SectionBody s={section} />
                </ClientName.Provider>
              ) : (
                <p className="text-sm text-muted">
                  There is no guide for this page yet. The full guide covers every tab.
                </p>
              )}
            </div>
            <div className="border-t border-border px-5 py-3">
              <Link
                to={`/p/${program.id}/guide${section ? `#${section.id}` : ''}`}
                className="inline-flex items-center gap-1.5 text-sm text-brand-text hover:underline"
              >
                <BookOpen className="size-4" /> Open the full guide <ExternalLink className="size-3" />
              </Link>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

/** The full guide: every tab this member can see, in sidebar order. */
export function GuidePage() {
  const program = useOutletContext<Program>();
  const sections = guideFor(program);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash]);
  return (
    <div className="mx-auto flex max-w-[1180px] gap-8 px-6 py-6">
      <nav className="sticky top-6 hidden h-fit w-52 shrink-0 lg:block" aria-label="Guide contents">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Contents</p>
        <ul className="space-y-0.5 text-[13px]">
          {sections.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                className={cn(
                  'block rounded px-2 py-1 hover:bg-surface-2',
                  hash === `#${s.id}` ? 'font-medium text-brand-text' : 'text-muted',
                )}
              >
                {s.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      <div className="min-w-0 flex-1 space-y-6">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Guide</h1>
          <p className="mt-1 text-sm text-muted">
            Step by step, for the tabs you use. Labels in boxes are what you will see on screen. Steps marked{' '}
            <Badge tone="warn">Outside HELM</Badge> happen by email, in a meeting or on paper. Every tab also has{' '}
            <span className="font-medium text-text">How to</span> at the top of the screen.
          </p>
        </div>
        {sections.map((s) => (
          <Card key={s.id} id={s.id} className="scroll-mt-6 p-5">
            <div className="mb-3 flex items-center gap-3">
              <h2 className="text-[17px] font-semibold">{s.title}</h2>
              {s.id !== 'account' && (
                <Link to={`/p/${program.id}/${s.id}`} className="ml-auto text-xs text-brand-text hover:underline">
                  Go to {s.title}
                </Link>
              )}
            </div>
            <ClientName.Provider value={program.clientShortName}>
              <SectionBody s={s} />
            </ClientName.Provider>
          </Card>
        ))}
      </div>
    </div>
  );
}
